import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";

const steps = [
  {
    number: "1",
    title: "Nous étudions votre projet dans les moindres détails",
    content: [
      "Nous avons besoin de comprendre votre quotidien, votre mode de vie et vos objectifs patrimoniaux afin de définir une stratégie claire et réaliste. Recevez-vous souvent ? Travaillez-vous de chez vous ? Aimez-vous cuisiner ? Les enfants auront-ils leur école, leur lycée à proximité ? …",
      "Nous analysons aussi les transports, la sectorisation scolaire et vos projets de famille.",
      "Nous vous aidons à poser les bons chiffres : capacité d'emprunt, frais de notaire, honoraires, travaux éventuels. Nous vous aidons à construire une simulation complète, à définir vos priorités et à identifier avec vous ce qui est essentiel.",
      "Lorsque votre projet est parfaitement cadré, nous établissons ensemble le cahier des charges qui guidera notre recherche, puis formalisons notre accompagnement par la signature du mandat de recherche."
    ]
  },
  {
    number: "2",
    title: "Nous recherchons et sélectionnons uniquement les meilleures opportunités",
    content: [
      "Nous effectuons un screening quotidien du marché afin d'identifier les biens réellement adaptés à votre recherche. Nous visitons, analysons et filtrons pour vous éviter des déplacements inutiles et vous faire gagner un temps précieux.",
      "Nous ne vous présentons que des biens pertinents, sélectionnés avec exigence selon vos critères, votre budget et vos objectifs."
    ],
    details: [
      "Veille active sur l'ensemble du marché et notre réseau off-market",
      "Pré-visites et analyses avant présentation",
      "Comptes rendus détaillés avec points forts, défauts et potentiel",
      "Accompagnement technique avec architecte ou artisans si nécessaire",
      "Étude du quartier, du marché et du potentiel du bien"
    ]
  },
  {
    number: "3",
    title: "Nous défendons vos intérêts jusqu'à la promesse de vente",
    content: [
      "Avant toute offre, nous réalisons une analyse complète du bien afin de sécuriser votre décision et négocier dans les meilleures conditions.",
      "Nous vous accompagnons sur tous les aspects techniques, juridiques et financiers afin que chaque étape soit maîtrisée et sécurisée."
    ],
    details: [
      "Analyse du prix et de la cohérence avec le marché",
      "Étude des diagnostics, copropriété et travaux éventuels",
      "Négociation argumentée du prix et des conditions",
      "Rédaction et sécurisation de l'offre d'achat",
      "Accompagnement jusqu'à la signature de la promesse de vente"
    ]
  },
  {
    number: "4",
    title: "Nous assurons un accompagnement complet jusqu'à la signature définitive",
    content: [
      "Notre mission ne s'arrête pas à l'acceptation de l'offre. Nous pilotons chaque étape jusqu'à la remise des clés afin de garantir une transaction fluide, sereine et sécurisée."
    ],
    details: [
      "Suivi administratif et financier du dossier",
      "Coordination avec notaires, banques, courtiers et diagnostiqueurs",
      "Organisation des échéances et vérifications",
      "Visite finale avant signature",
      "Présence à vos côtés chez le notaire"
    ]
  }
];

export const RecherchesPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  return (
    <div data-testid="recherches-page" className="pt-32">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-gold text-sm font-bold uppercase tracking-[0.2em] mb-4">
              Chasseur immobilier
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
              Nous trouvons pour vous le bien idéal !
            </h1>
            <p className="text-text-secondary text-lg leading-relaxed">
              Un appartement n'est pas seulement une surface ou une adresse : c'est un lieu de vie.
              Nous cherchons à comprendre votre quotidien, vos habitudes, vos contraintes et vos projets.
              Notre objectif : vous aider à choisir un bien qui corresponde vraiment à votre vie, pas seulement à votre budget.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hero Banner Image - terrasse */}
      <section className="py-0 bg-background">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto px-6 lg:px-8"
        >
          <div className="relative h-[300px] md:h-[420px] overflow-hidden">
            <img
              src="https://res.cloudinary.com/dpeldfqpi/image/upload/v1784553797/iStock-1463510971_wtw64q.jpg"
              alt="Recherche de bien immobilier à Paris"
              className="w-full h-full object-cover"
              data-testid="recherches-banner-img"
            />
          </div>
        </motion.div>
      </section>

      {/* Steps Section */}
      <section data-testid="steps-section" className="py-16 md:py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary text-center mb-16"
          >
            Les étapes de votre recherche
          </motion.h2>

          <div className="space-y-12">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`step-${index}`}
                className="bg-background border border-line p-8 md:p-12"
              >
                <div className="flex items-baseline gap-6 mb-6">
                  <span className="font-serif text-gold text-6xl leading-none">{step.number}</span>
                  <h3 className="font-serif text-text-primary text-2xl">
                    {step.title}
                  </h3>
                </div>
                <div className="space-y-4 text-text-secondary leading-relaxed">
                  {step.content.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
                {step.details && (
                  <div className="mt-6 pt-6 border-t border-line">
                    <p className="text-gold font-semibold mb-4">Concrètement :</p>
                    <ul className="space-y-2">
                      {step.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-text-secondary">
                          <span className="text-gold mt-1">•</span>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rémunération Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-surface border border-gold/30 p-8 md:p-12 text-center"
          >
            <h3 className="text-gold font-bold text-xl mb-4">
              Une rémunération alignée avec votre réussite
            </h3>
            <p className="text-text-secondary leading-relaxed">
              Notre rémunération n'intervient qu'au succès, lors de la signature définitive.
              Cela signifie une implication totale à chaque étape, avec un seul objectif : défendre vos intérêts et concrétiser votre projet dans les meilleures conditions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Bottom photo - integrated */}
      <section className="py-0 bg-background">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto px-6 lg:px-8 py-8"
        >
          <div
            data-testid="recherches-image-placeholder"
            className="relative h-[450px] overflow-hidden"
          >
            <img
              src="https://res.cloudinary.com/dpeldfqpi/image/upload/v1784555726/iStock-1125385998_uora2n.jpg"
              alt="Chasseur immobilier Paris"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-surface">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-6">
              Prêt à trouver votre bien idéal ?
            </h2>
            <Link to="/contact">
              <Button
                data-testid="cta-recherches-contact"
                className="bg-navy text-white hover:bg-navy hover:border-gold border border-navy font-semibold px-10 py-6 text-lg rounded-none"
              >
                Démarrer ma recherche
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
